'use client';

import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, Slider, Stack, Theme, Typography } from '@mui/material';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { formatPlaybackTime } from './formatPlaybackTime';

/**
 * Internal to `CaptureViewer`; never exported from the package. The clip plays in the stage the
 * still already occupies, with no second pop-up and no play triangle sitting over the customer's
 * face.
 *
 * Scrubbable through the framework Slider: click, drag and keyboard all set the video's own
 * `currentTime`, and the thumb follows playback through the same `currentTime` state the time label
 * reads.
 */
export type TInlinePlayerProps = {
  /** The clip. Plays in place of the still, at the same stage box. */
  src: string;
  /** The still shown until playback actually starts. */
  poster: ReactElement;
  /** The stage's target box, in pixels. Caps the video's rendered size; never enlarges it. */
  box: { width: number; height: number };
  /** Presents the stage on black, for video stills. Matches the still's own `onDark` so the
   * letterbox behind the poster and the letterbox behind the video, once it is playing, are the
   * same colour. */
  onDark?: boolean;
  /** Accessible name for the `<video>` element. Needed because the poster's `<img>` supplies the
   * only accessible name in this stage until playback starts, and the `<video>` has none of its
   * own once the poster unmounts. */
  label: string;
  /**
   * Called when the clip fails to load or play. This package has no logging utility and must not
   * grow one: this callback is the only way such a failure reaches the surrounding app.
   */
  onError?: () => void;
};

/**
 * **Takes no `data-testid`.** It is a capture stage inside the captures column, never placed by a
 * page, so per ADR 72 it is located through its ancestor. Its own `label` is the accessible name of
 * both the poster image and the video, which is what tells one player apart from another.
 *
 * **Portability.** This is a plain `<video>` with the native controls suppressed and our own drawn
 * over it, which is the widely supported path rather than a novel one. Three choices carry that:
 *
 * - `playsInline` stops iOS Safari hijacking playback into its fullscreen player, which is its
 *   default for an inline `<video>` and would take the customer out of the dialog entirely.
 * - `preload="metadata"` gets `duration` for the timer without pulling the whole clip down, so a
 *   dialog that opens on four captures does not fetch four videos nobody has pressed play on.
 * - Playback is click-initiated, never autoplaying, so no browser's autoplay policy applies and
 *   the player never needs to be muted to work.
 *
 * There is one `src` and no `<source type>` list, so there is no codec negotiation: the format is
 * whatever the backend serves. A browser that cannot decode it fires `error`, which restores the
 * still and shows "Playback failed" rather than leaving an empty black box.
 */
export default function InlinePlayer({
  src,
  poster,
  box,
  onDark,
  label,
  onError,
}: Readonly<TInlinePlayerProps>): ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasErrored, setHasErrored] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Bound to the element's own events rather than set when we call play(): the clip also ends on
  // its own, and a control that only tracks our own calls then sits on Pause over a stopped video.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onDurationChange = () => setDuration(video.duration);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    // Playback has actually started rendering frames, not merely been requested. This, not the
    // click that requested playback, is the point at which it is safe to retire the poster:
    // setting `hasStarted` any earlier would hide the only visible evidence before the clip has
    // proven it can play at all.
    const onPlaying = () => setHasStarted(true);
    // A seek made before the first play completes with the frame decoded at the new position, so
    // the video, not the poster, is what shows it. `seeked` rather than the seek call itself, for
    // the same reason as `playing` above.
    const onSeeked = () => setHasStarted(true);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(video.duration);
    };
    // The element's own authoritative signal that the clip cannot play: a missing file, a 404, an
    // unsupported codec. Bring the poster back (it is still real evidence) and switch the control
    // bar to a visible failure state, rather than leaving hasStarted permanently true over a
    // video element with nothing to show.
    const onVideoError = () => {
      setIsPlaying(false);
      setHasStarted(false);
      setHasErrored(true);
      onError?.();
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('loadedmetadata', onDurationChange);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onVideoError);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('loadedmetadata', onDurationChange);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onVideoError);
    };
  }, [onError]);

  function togglePlayback() {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      // A rejection here (an autoplay policy, or a play() interrupted by a fast second click) is
      // not on its own proof the clip is broken: the 'error' listener above is the element's own
      // authoritative signal, and drives the failure state. This catch only stops the rejection
      // surfacing as an unhandled promise rejection.
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }

  // A live stream or a recorder-written clip reports `Infinity`, and nothing is seekable then.
  const canSeek = Number.isFinite(duration) && duration > 0;

  function seekTo(_event: Event, value: number | number[]) {
    const video = videoRef.current;
    if (video && typeof value === 'number') {
      video.currentTime = value;
      // The thumb is controlled by `currentTime`, which otherwise only catches up on the next
      // `timeupdate`; a second input before then would start from the stale value and be lost.
      setCurrentTime(value);
    }
  }

  return (
    // Fixed to the stage box rather than `fit-content`: this is the one sizing context both the
    // poster and the video fill (each via its own object-fit: contain), so the switch from still
    // to video never changes the stage's footprint. Before this, the still and the video each
    // computed their own rendered size from their own intrinsic aspect ratio, and a capture whose
    // poster ratio differed from the video's (routine: the ID portrait is a tall rectangle, the
    // liveness capture can be landscape) visibly jumped the moment playback started.
    <Box
      sx={(t: Theme) => ({
        position: 'relative',
        width: box.width,
        height: box.height,
        flex: 'none',
        backgroundColor: onDark ? t.palette.common.black : t.palette.background.default,
      })}
    >
      {/* The still stays mounted underneath until the clip actually starts playing, so the stage
          never flashes empty while the clip buffers its first frame, and comes back on its own
          (see the 'error' listener above) if the clip then fails outright. */}
      {!hasStarted && (
        <Box sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>{poster}</Box>
      )}
      <video
        ref={videoRef}
        src={src}
        aria-label={label}
        playsInline
        preload="metadata"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          visibility: hasStarted ? 'visible' : 'hidden',
        }}
      />

      {/* Overlaid on the capture rather than placed under it, over a gradient that fades upwards.
          Controls in a bar of their own would add height below the capture and push the label
          out of line with the capture beside it, which is the one comparison this row is for.
          The gradient is what keeps the controls legible over a light frame. */}
      <Stack
        direction="row"
        alignItems="center"
        sx={(t: Theme) => ({
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          gap: t.semantic.spacing.xs,
          padding: t.semantic.spacing['2xs'],
          background: `linear-gradient(to top, ${t.palette.mediaOverlay.scrim}, transparent)`,
        })}
      >
        {hasErrored ? (
          <Typography variant="caption" sx={(t: Theme) => ({ color: t.palette.common.white })}>
            Playback failed
          </Typography>
        ) : (
          <>
            <IconButton
              size="small"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              data-action="toggle-capture-playback"
              onClick={togglePlayback}
              sx={(t: Theme) => ({
                flex: 'none',
                color: t.palette.common.white,
                backgroundColor: t.palette.mediaOverlay.control,
                '&:hover': { backgroundColor: t.palette.mediaOverlay.controlHover },
              })}
            >
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>

            <Slider
              size="small"
              aria-label="Playback progress"
              min={0}
              max={canSeek ? duration : 1}
              value={canSeek ? currentTime : 0}
              disabled={!canSeek}
              getAriaValueText={(seconds) =>
                `${formatPlaybackTime(seconds)} of ${formatPlaybackTime(duration)}`
              }
              step={0.1}
              shiftStep={1}
              onChange={seekTo}
              sx={(t: Theme) => ({
                color: t.palette.common.white,
                '& .MuiSlider-rail': {
                  opacity: 1,
                  backgroundColor: t.palette.mediaOverlay.track,
                },
                // The default halo is the primary colour at low alpha, which disappears on the
                // black scrim; the overlay's own halo tint is what reads there.
                '& .MuiSlider-thumb': {
                  '&:hover, &.Mui-focusVisible, &.Mui-active': {
                    boxShadow: `0 0 0 ${t.semantic.spacing.xs} ${t.palette.mediaOverlay.halo}`,
                  },
                },
              })}
            />

            <Typography
              variant="caption"
              sx={(t: Theme) => ({
                color: t.palette.common.white,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              })}
            >
              {`${formatPlaybackTime(currentTime)} / ${formatPlaybackTime(duration)}`}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}
